import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AuthLayout from '../components/AuthLayout';
import clientService, { Client, ClientParams } from '../services/client.service';

const ClientList: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalClients, setTotalClients] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientTypeFilter, setClientTypeFilter] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<string>('true');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const fetchClients = async (params: ClientParams = {}) => {
    try {
      setLoading(true);
      setError('');
      
      const response = await clientService.getClients({
        page: page + 1, // API uses 1-based indexing
        limit: rowsPerPage,
        search: searchTerm,
        clientType: clientTypeFilter || undefined,
        isActive: activeFilter ? activeFilter === 'true' : undefined,
        ...params
      });
      
      setClients(response.clients);
      setTotalClients(response.pagination.total);
    } catch (err: any) {
      console.error('Error fetching clients:', err);
      setError('Falha ao carregar clientes. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [page, rowsPerPage, searchTerm, clientTypeFilter, activeFilter]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleClientTypeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setClientTypeFilter(event.target.value as string);
    setPage(0);
  };

  const handleActiveFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setActiveFilter(event.target.value as string);
    setPage(0);
  };

  const handleAddClient = () => {
    navigate('/clients/new');
  };

  const handleEditClient = (clientId: string) => {
    navigate(`/clients/edit/${clientId}`);
  };

  const handleViewClient = (clientId: string) => {
    navigate(`/clients/${clientId}`);
  };

  const handleDeleteClick = (client: Client) => {
    setClientToDelete(client);
    setDeleteDialogOpen(true);
    setDeleteError('');
  };

  const handleDeleteConfirm = async () => {
    if (!clientToDelete) return;
    
    try {
      setDeleteLoading(true);
      setDeleteError('');
      
      await clientService.deleteClient(clientToDelete._id as string);
      
      setDeleteDialogOpen(false);
      setClientToDelete(null);
      
      // Refresh client list
      fetchClients();
    } catch (err: any) {
      console.error('Error deleting client:', err);
      
      if (err.response?.data?.error === 'client_has_assets') {
        setDeleteError(`Este cliente possui ${err.response.data.assetCount} ativos vinculados e não pode ser excluído.`);
      } else {
        setDeleteError('Falha ao excluir cliente. Por favor, tente novamente.');
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setClientToDelete(null);
    setDeleteError('');
  };

  const getClientTypeLabel = (type: string) => {
    switch (type) {
      case 'individual':
        return 'Pessoa Física';
      case 'company':
        return 'Empresa';
      case 'government':
        return 'Governo';
      case 'other':
        return 'Outro';
      default:
        return type;
    }
  };

  const getDocumentTypeLabel = (type: string) => {
    return type === 'CPF' ? 'CPF' : 'CNPJ';
  };

  return (
    <AuthLayout title="Clientes">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 500 }}>
            Clientes
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddClient}
            sx={{ 
              backgroundColor: '#2e7d32',
              '&:hover': {
                backgroundColor: '#1b5e20',
              }
            }}
          >
            Novo Cliente
          </Button>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: { xs: 1, sm: 2 }, mb: 3 }}>
          <TextField
            label="Buscar"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ flexGrow: 1, minWidth: '200px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <FormControl variant="outlined" size="small" sx={{ minWidth: '200px' }}>
            <InputLabel id="client-type-label">Tipo de Cliente</InputLabel>
            <Select
              labelId="client-type-label"
              value={clientTypeFilter}
              // @ts-ignore - Incompatibilidade de tipos entre React.ChangeEvent e SelectChangeEvent
              onChange={handleClientTypeChange}
              label="Tipo de Cliente"
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="individual">Pessoa Física</MenuItem>
              <MenuItem value="company">Empresa</MenuItem>
              <MenuItem value="government">Governo</MenuItem>
              <MenuItem value="other">Outro</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl variant="outlined" size="small" sx={{ minWidth: '150px' }}>
            <InputLabel id="active-filter-label">Status</InputLabel>
            <Select
              labelId="active-filter-label"
              value={activeFilter}
              // @ts-ignore - Incompatibilidade de tipos entre React.ChangeEvent e SelectChangeEvent
              onChange={handleActiveFilterChange}
              label="Status"
            >
              <MenuItem value="true">Ativos</MenuItem>
              <MenuItem value="false">Inativos</MenuItem>
              <MenuItem value="">Todos</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
      
      {error && (
        <Box sx={{ mb: 3 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}
      
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: { xs: 'calc(100vh - 250px)', sm: 'calc(100vh - 300px)' }, overflowX: 'auto' }}>
          <Table stickyHeader aria-label="sticky table" size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
                <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', sm: 'table-cell' } }}>Documento</TableCell>
                <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}>Cidade/UF</TableCell>
                <TableCell sx={{ fontWeight: 'bold', display: { xs: 'none', lg: 'table-cell' } }} align="center">Localização</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={40} sx={{ color: '#2e7d32' }} />
                  </TableCell>
                </TableRow>
              ) : clients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nenhum cliente encontrado
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                clients.map((client) => (
                  <TableRow hover key={client._id}>
                    <TableCell>{client.name}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {getDocumentTypeLabel(client.documentType)}: {client.documentNumber}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{getClientTypeLabel(client.clientType)}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {client.address?.city}/{client.address?.state}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }} align="center">
                      {client.address?.coordinates?.lat && client.address?.coordinates?.lng ? (
                        <Tooltip title={`Lat: ${client.address.coordinates.lat}, Lng: ${client.address.coordinates.lng}`}>
                          <LocationOnIcon sx={{ color: '#4caf50', fontSize: 24 }} />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Coordenadas não cadastradas">
                          <LocationOnIcon sx={{ color: '#bdbdbd', fontSize: 24 }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={client.isActive ? "Ativo" : "Inativo"} 
                        color={client.isActive ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ p: { xs: 0.5, sm: 1 }, pr: { xs: 1, sm: 2 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: { xs: 0, sm: 0.5 } }}>
                        <Tooltip title="Visualizar">
                          <IconButton 
                            size="small" 
                            onClick={() => handleViewClient(client._id as string)}
                            sx={{ color: '#2196f3', p: { xs: 0.5, sm: 1 } }}
                          >
                            <VisibilityIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton 
                            size="small" 
                            onClick={() => handleEditClient(client._id as string)}
                            sx={{ color: '#ff9800', p: { xs: 0.5, sm: 1 } }}
                          >
                            <EditIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteClick(client)}
                            sx={{ color: '#f44336', p: { xs: 0.5, sm: 1 } }}
                          >
                            <DeleteIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalClients}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Itens por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir o cliente "{clientToDelete?.name}"?
            Esta ação não pode ser desfeita.
          </DialogContentText>
          {deleteError && (
            <Box sx={{ mt: 2 }}>
              <Typography color="error">{deleteError}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : null}
          >
            {deleteLoading ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </AuthLayout>
  );
};

export default ClientList;
